import React, {MouseEvent, useCallback, useContext, useEffect, useRef, useState} from 'react';
import cx from 'classnames';
import {Icon, Text, Title, ToggleSwitch} from '@priority-software/priority-style-react';
import {priority} from '@priority-software/priority-proto-api/lib';
import {ReactComponent as ArrowIcon} from './assets/arrow-bg.svg';
import {ToggleButton} from '../../../../../../components/ToggleButton';
import {SettingsActions} from '../SettingsActions/SettingsActions';
import {AutomationRuleConditionList} from '../AutomationRuleConditionList';
import {AutomationActionTemplate} from '../AutomationActionTemplate';
import {IAlert, IAlertConditionWithID} from '../../../../actions/proto';
import {InputControl} from '../AutomationRuleCondition/InputControl';
import {AlertFields, RuleType} from '../../../../enums';
import {AutomationContext} from '../../../../context';
import {PanelBlock} from '../../../../../../components/Common/PanelBlock/PanelBlock';
import {AutofillActionContent} from './AutofillActionContent';
import {MAX_DESCRIPTION_CHAR_COUNT} from './constants';
import {CheckedValue} from '../../../../../../components/ToggleButton/enum';
import {emptyRecipientFields, loaderSize, ruleDialogID} from '../../../../constants';
import {texts} from '../../../../texts';
import {prepareChatboxData} from './utils';
import {createDataTestId} from '../../../../../../utils/dataTestId';
import {AIAskBlock} from '../../../common/AIAskBlock/AIAskBlock';
import {supportAIBlock} from '../../../../utils';
import {RuleDialogContext} from '../../../RulesListDialog/context';
import {AILoader} from '../../../../../../components/AILoader';
import styles from './style.module.scss';

type Props = {
    rule: IAlert;
    ruleType: RuleType;
    onRuleUpdate: (data: Partial<IAlert>) => void;
    isRuleActive: boolean;
    conditionOperator: CheckedValue;
    conditions: Array<IAlertConditionWithID>;
    addNewCondition: (condition: IAlertConditionWithID) => void;
    onChangeCondition: (id: string, data: Partial<IAlertConditionWithID>) => void;
    removeCondition: (id: string) => void;
    toggleConditionOperator: (checked: CheckedValue) => void;
    toggleActive: () => void;
    handleRuleFormData: (data: Partial<priority.netitems.form.IAlertForm>) => void;
    specialCondition?: React.ReactElement;
    session: string;
};

export const RuleContent = (props: Props) => {
    const {choosesOptions, openRule, fromStatus, toStatus} = useContext(AutomationContext);
    const {MessageTypes, Fields, ValueFields, CurrrentValues, Ops} = choosesOptions;

    const {
        session,
        rule,
        ruleType,
        conditionOperator,
        isRuleActive,
        conditions,
        onRuleUpdate,
        handleRuleFormData,
        toggleActive,
        addNewCondition,
        toggleConditionOperator,
        onChangeCondition,
        removeCondition,
        specialCondition
    } = props;
    const {aiLoading, setAiLoading} = useContext(RuleDialogContext);

    const chatboxData = prepareChatboxData(rule, MessageTypes, Boolean(!fromStatus && toStatus));
    const [activeAction, setActiveAction] = useState(
        chatboxData.find(item => item.value === rule.MessageType) || null
    );

    const inputRef = useRef<HTMLInputElement>(null);

    const handleActionClick = useCallback(
        (e: MouseEvent<HTMLButtonElement>) => {
            const selectedAction = chatboxData.find(
                item => item.value === Number(e.currentTarget.dataset.value)
            );

            if (selectedAction) {
                onRuleUpdate({
                    MessageType: selectedAction.value as number,
                    ...emptyRecipientFields
                });
                setActiveAction(selectedAction);
            }
        },
        [onRuleUpdate, chatboxData]
    );

    const handleInputValue = useCallback(
        (value: string, name: string) => {
            onRuleUpdate({[name]: value});
        },
        [onRuleUpdate]
    );

    useEffect(() => {
        requestAnimationFrame(() => {
            inputRef.current?.focus();
        });
    }, []);

    return (
        <>
            {!rule.AlertNumber && supportAIBlock() ? (
                <AIAskBlock
                    showOnlyInput
                    type={ruleType}
                    onLoadingChange={setAiLoading}
                    session={session}
                />
            ) : null}

            <div className={cx(styles.ruleContent, {[styles.isDisabled]: aiLoading})}>
                {aiLoading ? (
                    <div className={styles.loader}>
                        <AILoader width={loaderSize} height={loaderSize} />
                    </div>
                ) : null}

                <div className={styles.header}>
                    <div className={cx(styles.headerItem)}>
                        <Text
                            {...createDataTestId('rule-number-title', true)}
                            componentClasses={cx(styles.itemLabel, styles.nowrap)}
                            tag="div"
                            skin="paragraph-1"
                            textColor="primary-700"
                        >
                            {texts.rule_number_label}
                        </Text>
                        <div className={styles.headerItemElement}>
                            <Title
                                componentClasses={styles.nowrap}
                                skin="h4"
                                textColor="primary-700"
                                {...createDataTestId('rule-number-value', true)}
                            >
                                {rule.AlertNumber ? (
                                    rule.AlertNumber.toString()
                                ) : (
                                    <span className={styles.newLabel}>{texts.new_label}</span>
                                )}
                            </Title>
                        </div>
                    </div>

                    <div className={cx(styles.headerItem, styles.inputWrap)}>
                        <Text
                            componentClasses={cx(styles.nowrap, styles.itemLabel)}
                            tag="div"
                            skin="paragraph-1"
                            textColor="primary-700"
                            {...createDataTestId('rule-description-title', true)}
                        >
                            {texts.rule_description_label}
                        </Text>

                        <div
                            className={styles.headerItemElement}
                            {...createDataTestId('rule-description-value', false)}
                        >
                            <InputControl
                                name={AlertFields.Description}
                                size="large"
                                inputRef={inputRef}
                                maxChars={MAX_DESCRIPTION_CHAR_COUNT}
                                onChange={handleInputValue}
                                defaultValue={rule.Description}
                                placeholder={texts.rule_description_placeholder}
                            />
                        </div>
                    </div>

                    <div className={styles.headerItem}>
                        <div className={styles.headerItemElement}>
                            <Text
                                {...createDataTestId('rule-active-title', true)}
                                componentClasses={styles.nowrap}
                                skin="paragraph-1"
                                tag="div"
                                textColor="primary-700"
                            >
                                {texts.single_rule_active_label}
                            </Text>
                        </div>
                    </div>

                    <div className={styles.headerItem}>
                        <div className={styles.headerItemElement}>
                            <ToggleSwitch
                                onChange={toggleActive}
                                checked={isRuleActive}
                                {...createDataTestId('rule-active-toggle', true)}
                            />
                        </div>
                    </div>
                </div>

                <div className={styles.component}>
                    <PanelBlock
                        dataTestId="rule-when-section"
                        fixedHeader={
                            <div className={styles.sectionHeader}>
                                <Title
                                    skin="h4"
                                    fontWeight="semi-bold"
                                    {...createDataTestId('common-title', true)}
                                >
                                    {texts.when_label}
                                </Title>

                                <ToggleButton
                                    size="small"
                                    checked={conditionOperator}
                                    onChange={toggleConditionOperator}
                                    left={texts.operator_and_label}
                                    right={texts.operator_or_label}
                                />
                            </div>
                        }
                    >
                        <AutomationRuleConditionList
                            triggerField={openRule?.Form?.UpdateField as number}
                            session={session}
                            ruleType={ruleType}
                            isOptions={Ops}
                            ofOptions={Fields}
                            autofillFieldOptions={ValueFields}
                            ifOptions={CurrrentValues}
                            conditions={conditions}
                            addNewCondition={addNewCondition}
                            removeCondition={removeCondition}
                            onChangeCondition={onChangeCondition}
                            handleUpdateField={handleRuleFormData}
                            specialCondition={specialCondition}
                        />
                    </PanelBlock>

                    <ArrowIcon className={styles.arrowBg} width={40} height={40} />

                    <PanelBlock
                        dataTestId="rule-then-section"
                        fixedHeader={
                            <div className={styles.sectionHeader}>
                                <Title
                                    skin="h4"
                                    fontWeight="semi-bold"
                                    {...createDataTestId('common-title', true)}
                                >
                                    {texts.then_label}
                                </Title>

                                {!!activeAction && (
                                    <Text
                                        componentClasses={styles.headerAction}
                                        skin="caption-2"
                                        fontWeight="normal"
                                        tag="span"
                                        textColor="secondary-500"
                                    >
                                        <Icon
                                            componentClasses={styles.headerActionIcon}
                                            icon={activeAction.icon}
                                        />
                                        {activeAction.label}
                                    </Text>
                                )}
                            </div>
                        }
                    >
                        {ruleType === RuleType.autofill ? (
                            <AutofillActionContent
                                fieldsOptions={ValueFields}
                                setFieldValue={openRule.RecipientField as number}
                                autoFillValue={openRule.RecipientMail?.[0]} // Please, no judge, Backend team quirks
                                onRuleUpdate={onRuleUpdate}
                            />
                        ) : (
                            <>
                                <SettingsActions
                                    data={chatboxData}
                                    activeAction={activeAction?.value as number}
                                    handleClick={handleActionClick}
                                    tooltipPortalId={ruleDialogID}
                                />

                                <div className={styles.divider} />

                                <AutomationActionTemplate
                                    key={activeAction?.value as string}
                                    onRuleUpdate={onRuleUpdate}
                                    activeAction={activeAction?.value as number}
                                />
                            </>
                        )}
                    </PanelBlock>
                </div>
            </div>
        </>
    );
};
